import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import './AdvertiserForm.css';

interface Affiliate {
  id: number;
  name: string;
  company: string;
  email: string;
  status: 'pending' | 'approved' | 'blocked';
}

const EditAdvertiser: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [formData, setFormData] = useState({
    name: '',
    contact_email: '',
    postback_url_template: '',
    secret_key: '',
    postback_auth_method: 'none',
    whitelisted_ips: '',
    billing_first_name: '',
    billing_last_name: '',
    billing_phone: '',
    billing_address: '',
    billing_city: '',
    billing_state: '',
    billing_country: '',
    status: 'active',
    monthly_budget: '',
    budget_alert_threshold: 80
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Affiliate approval management
  const [allAffiliates, setAllAffiliates] = useState<Affiliate[]>([]);
  const [affiliateStatuses, setAffiliateStatuses] = useState<Record<number, 'approved' | 'blocked'>>({});
  const [affiliateSearch, setAffiliateSearch] = useState('');
  const [selectedApprovedAffiliates, setSelectedApprovedAffiliates] = useState<Set<number>>(new Set());
  const [selectedBlockedAffiliates, setSelectedBlockedAffiliates] = useState<Set<number>>(new Set());

  useEffect(() => {
    const fetchAdvertiser = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const response = await apiService.getAdvertiser(parseInt(id));
        const advertiser = response.advertiser;

        // Parse billing_contact if it's a JSON object
        const billingContact = typeof advertiser.billing_contact === 'object'
          ? advertiser.billing_contact
          : (typeof advertiser.billing_contact === 'string' && advertiser.billing_contact
            ? JSON.parse(advertiser.billing_contact)
            : {});

        setFormData({
          name: advertiser.name || '',
          contact_email: advertiser.contact_email || '',
          postback_url_template: advertiser.postback_url_template || '',
          secret_key: advertiser.secret_key || '',
          postback_auth_method: advertiser.postback_auth_method || 'hmac',
          whitelisted_ips: Array.isArray(advertiser.whitelisted_ips) ? advertiser.whitelisted_ips.join(', ') : '',
          billing_first_name: billingContact.first_name || '',
          billing_last_name: billingContact.last_name || '',
          billing_phone: billingContact.phone || '',
          billing_address: billingContact.address || '',
          billing_city: billingContact.city || '',
          billing_state: billingContact.state || '',
          billing_country: billingContact.country || '',
          status: advertiser.status || 'active',
          monthly_budget: advertiser.monthly_budget || '',
          budget_alert_threshold: advertiser.budget_alert_threshold || 80
        });
        // Fetch publishers with approval status for this advertiser
        const publishersResponse = await apiService.getAdvertiserPublishers(parseInt(id));

        // Map publishers to affiliates format with approval status
        const affiliates = publishersResponse.publishers.map((publisher: any) => ({
          id: publisher.id,
          name: publisher.name || '',
          company: publisher.name || '',
          email: publisher.email || '',
          status: publisher.approval_status || 'blocked'
        }));
        setAllAffiliates(affiliates);

        // Build affiliate status map
        const statusMap: Record<number, 'approved' | 'blocked'> = {};
        affiliates.forEach((aff: Affiliate) => {
          statusMap[aff.id] = aff.status as 'approved' | 'blocked';
        });
        setAffiliateStatuses(statusMap);

      } catch (err: any) {
        setError(err.message || 'Failed to fetch advertiser');
      } finally {
        setLoading(false);
      }
    };

    fetchAdvertiser();
  }, [id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!id) return;

    try {
      setLoading(true);
      const updateData = {
        name: formData.name,
        contact_email: formData.contact_email,
        postback_url_template: formData.postback_url_template,
        secret_key: formData.secret_key,
        postback_auth_method: formData.postback_auth_method,
        whitelisted_ips: formData.whitelisted_ips.split(',').map(ip => ip.trim()).filter(ip => ip),
        billing_contact: {
          first_name: formData.billing_first_name,
          last_name: formData.billing_last_name,
          email: formData.contact_email,
          phone: formData.billing_phone,
          address: formData.billing_address,
          city: formData.billing_city,
          state: formData.billing_state,
          country: formData.billing_country
        },
        status: formData.status,
        monthly_budget: formData.monthly_budget ? parseFloat(formData.monthly_budget) : null,
        budget_alert_threshold: parseInt(formData.budget_alert_threshold.toString())
      };

      await apiService.updateAdvertiser(parseInt(id), updateData);
      alert('Advertiser updated successfully!');
      navigate('/advertisers');
    } catch (err: any) {
      setError(err.message || 'Failed to update advertiser');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/advertisers');
  };

  // Toggle individual affiliate status
  const toggleAffiliateStatus = async (affiliateId: number) => {
    if (!id) return;

    const currentStatus = affiliateStatuses[affiliateId];
    const newStatus = currentStatus === 'approved' ? 'blocked' : 'approved';

    try {
      if (newStatus === 'approved') {
        await apiService.approvePublisherForAdvertiser(parseInt(id), affiliateId);
      } else {
        await apiService.blockPublisherForAdvertiser(parseInt(id), affiliateId);
      }

      setAffiliateStatuses(prev => ({
        ...prev,
        [affiliateId]: newStatus
      }));
    } catch (err: any) {
      alert(`Failed to update affiliate status: ${err.message}`);
    }
  };

  // Approve all affiliates
  const handleApproveAllAffiliates = async () => {
    if (!id) return;

    const blockedAffiliates = filteredAffiliates.filter(aff => affiliateStatuses[aff.id] === 'blocked');
    if (blockedAffiliates.length === 0) {
      alert('No blocked affiliates to approve');
      return;
    }

    try {
      for (const aff of blockedAffiliates) {
        await apiService.approvePublisherForAdvertiser(parseInt(id), aff.id);
      }

      const newStatuses = { ...affiliateStatuses };
      blockedAffiliates.forEach(aff => {
        newStatuses[aff.id] = 'approved';
      });
      setAffiliateStatuses(newStatuses);

      alert(`${blockedAffiliates.length} affiliates approved successfully!`);
    } catch (err: any) {
      alert(`Failed to approve all affiliates: ${err.message}`);
    }
  };

  // Block all affiliates
  const handleBlockAllAffiliates = async () => {
    if (!id) return;

    const approvedAffiliates = filteredAffiliates.filter(aff => affiliateStatuses[aff.id] === 'approved');
    if (approvedAffiliates.length === 0) {
      alert('No approved affiliates to block');
      return;
    }

    try {
      for (const aff of approvedAffiliates) {
        await apiService.blockPublisherForAdvertiser(parseInt(id), aff.id);
      }

      const newStatuses = { ...affiliateStatuses };
      approvedAffiliates.forEach(aff => {
        newStatuses[aff.id] = 'blocked';
      });
      setAffiliateStatuses(newStatuses);

      alert(`${approvedAffiliates.length} affiliates blocked successfully!`);
    } catch (err: any) {
      alert(`Failed to block all affiliates: ${err.message}`);
    }
  };

  // Multi-select handlers
  const toggleApprovedSelection = (affiliateId: number) => {
    setSelectedApprovedAffiliates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(affiliateId)) {
        newSet.delete(affiliateId);
      } else {
        newSet.add(affiliateId);
      }
      return newSet;
    });
  };

  const toggleBlockedSelection = (affiliateId: number) => {
    setSelectedBlockedAffiliates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(affiliateId)) {
        newSet.delete(affiliateId);
      } else {
        newSet.add(affiliateId);
      }
      return newSet;
    });
  };

  const handleApproveSelected = async () => {
    if (!id || selectedBlockedAffiliates.size === 0) {
      alert('No affiliates selected');
      return;
    }

    const affiliateIds = Array.from(selectedBlockedAffiliates);

    try {
      for (const affiliateId of affiliateIds) {
        await apiService.approvePublisherForAdvertiser(parseInt(id), affiliateId);
      }

      const newStatuses = { ...affiliateStatuses };
      affiliateIds.forEach(affiliateId => {
        newStatuses[affiliateId] = 'approved';
      });
      setAffiliateStatuses(newStatuses);
      setSelectedBlockedAffiliates(new Set());

      alert(`${affiliateIds.length} affiliates approved successfully!`);
    } catch (err: any) {
      alert(`Failed to approve selected affiliates: ${err.message}`);
    }
  };

  const handleBlockSelected = async () => {
    if (!id || selectedApprovedAffiliates.size === 0) {
      alert('No affiliates selected');
      return;
    }

    const affiliateIds = Array.from(selectedApprovedAffiliates);

    try {
      for (const affiliateId of affiliateIds) {
        await apiService.blockPublisherForAdvertiser(parseInt(id), affiliateId);
      }

      const newStatuses = { ...affiliateStatuses };
      affiliateIds.forEach(affiliateId => {
        newStatuses[affiliateId] = 'blocked';
      });
      setAffiliateStatuses(newStatuses);
      setSelectedApprovedAffiliates(new Set());

      alert(`${affiliateIds.length} affiliates blocked successfully!`);
    } catch (err: any) {
      alert(`Failed to block selected affiliates: ${err.message}`);
    }
  };

  // Filter affiliates based on search
  const filteredAffiliates = allAffiliates.filter(affiliate =>
    affiliate.name.toLowerCase().includes(affiliateSearch.toLowerCase()) ||
    affiliate.company.toLowerCase().includes(affiliateSearch.toLowerCase()) ||
    affiliate.email.toLowerCase().includes(affiliateSearch.toLowerCase())
  );

  const approvedAffiliates = filteredAffiliates.filter(affiliate => affiliateStatuses[affiliate.id] === 'approved');
  const blockedAffiliates = filteredAffiliates.filter(affiliate => affiliateStatuses[affiliate.id] === 'blocked');

  return (
    <div className="advertiser-form-page">
      <div className="form-header">
        <h1>Edit Advertiser</h1>
        <button
          className="btn btn-secondary"
          onClick={handleCancel}
        >
          ← Back to Advertisers
        </button>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit} className="advertiser-form">
          <div className="form-grid">
            {/* Left Column */}
            <div className="form-column">
              <div className="form-group">
                <label htmlFor="name">Name*</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="contact_email">Contact Email*</label>
                <input
                  type="email"
                  id="contact_email"
                  name="contact_email"
                  value={formData.contact_email}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="billing_first_name">Billing First Name</label>
                <input
                  type="text"
                  id="billing_first_name"
                  name="billing_first_name"
                  value={formData.billing_first_name}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="billing_last_name">Billing Last Name</label>
                <input
                  type="text"
                  id="billing_last_name"
                  name="billing_last_name"
                  value={formData.billing_last_name}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="billing_phone">Billing Phone</label>
                <input
                  type="tel"
                  id="billing_phone"
                  name="billing_phone"
                  value={formData.billing_phone}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="status">Status*</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="form-select"
                  required
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="monthly_budget">Monthly Budget</label>
                <input
                  type="number"
                  id="monthly_budget"
                  name="monthly_budget"
                  value={formData.monthly_budget}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="0"
                  step="0.01"
                  min="0"
                />
              </div>
            </div>

            {/* Right Column */}
            <div className="form-column">
              <div className="form-group">
                <label htmlFor="secret_key">Secret Key</label>
                <input
                  type="text"
                  id="secret_key"
                  name="secret_key"
                  value={formData.secret_key}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="postback_auth_method">
                  Postback Auth Method*
                  <span style={{ fontSize: '11px', color: '#a0aec0', marginLeft: '8px' }}>
                    (How advertiser proves conversions)
                  </span>
                </label>
                <select
                  id="postback_auth_method"
                  name="postback_auth_method"
                  value={formData.postback_auth_method}
                  onChange={handleInputChange}
                  className="form-select"
                  required
                >
                  <option value="hmac">HMAC Signature (Most Secure)</option>
                  <option value="ip_whitelist">IP Whitelist (Medium Security)</option>
                  <option value="api_key">API Key Only (Low Security)</option>
                  <option value="none">No Authentication (No Security)</option>
                </select>
                <div style={{ marginTop: '6px', fontSize: '11px', color: '#a0aec0' }}>
                  {formData.postback_auth_method === 'hmac' && (
                    <span>✅ <strong>HMAC:</strong> Requires cryptographic signatures</span>
                  )}
                  {formData.postback_auth_method === 'ip_whitelist' && (
                    <span>⚠️ <strong>IP Whitelist:</strong> Only accepts from specific IPs (enter below)</span>
                  )}
                  {formData.postback_auth_method === 'api_key' && (
                    <span>⚠️ <strong>API Key Only:</strong> Minimal security</span>
                  )}
                  {formData.postback_auth_method === 'none' && (
                    <span>🚨 <strong>No Authentication:</strong> No security checks. Risk of fake conversions!</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="budget_alert_threshold">Budget Alert Threshold (%)</label>
                <input
                  type="number"
                  id="budget_alert_threshold"
                  name="budget_alert_threshold"
                  value={formData.budget_alert_threshold}
                  onChange={handleInputChange}
                  className="form-input"
                  min="0"
                  max="100"
                />
              </div>
            </div>
          </div>

          {/* Billing Address Section */}
          <div className="form-group full-width">
            <label htmlFor="billing_address">Billing Address</label>
            <input
              type="text"
              id="billing_address"
              name="billing_address"
              value={formData.billing_address}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter billing address"
            />
          </div>

          <div className="form-grid">
            <div className="form-column">
              <div className="form-group">
                <label htmlFor="billing_city">City</label>
                <input
                  type="text"
                  id="billing_city"
                  name="billing_city"
                  value={formData.billing_city}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="billing_country">Country</label>
                <input
                  type="text"
                  id="billing_country"
                  name="billing_country"
                  value={formData.billing_country}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-column">
              <div className="form-group">
                <label htmlFor="billing_state">State</label>
                <input
                  type="text"
                  id="billing_state"
                  name="billing_state"
                  value={formData.billing_state}
                  onChange={handleInputChange}
                  className="form-input"
                />
              </div>
            </div>
          </div>

          {/* Full Width Fields */}
          <div className="form-group full-width">
            <label htmlFor="postback_url_template">Postback URL Template</label>
            <input
              type="url"
              id="postback_url_template"
              name="postback_url_template"
              value={formData.postback_url_template}
              onChange={handleInputChange}
              className="form-input"
              placeholder="https://example.com/postback?click_id={click_id}&payout={payout}"
            />
            <small className="field-hint">
              Use macros: {'{click_id}'}, {'{payout}'}, {'{conversion_id}'}
            </small>
          </div>

          {formData.postback_auth_method === 'ip_whitelist' && (
            <div className="form-group full-width">
              <label htmlFor="whitelisted_ips">
                Whitelist IP Addresses*
                <span style={{ fontSize: '12px', color: '#a0aec0', marginLeft: '10px' }}>
                  (Required for IP Whitelist authentication)
                </span>
              </label>
              <textarea
                id="whitelisted_ips"
                name="whitelisted_ips"
                value={formData.whitelisted_ips}
                onChange={handleInputChange}
                className="form-textarea"
                rows={3}
                placeholder="Enter IP addresses separated by commas (e.g., 203.0.113.45, 198.51.100.22)"
                required={formData.postback_auth_method === 'ip_whitelist'}
              />
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#a0aec0' }}>
                Only conversions from these IP addresses will be accepted. Separate multiple IPs with commas.
              </div>
            </div>
          )}

          {formData.postback_auth_method === 'hmac' && (
            <div className="form-group full-width">
              <label htmlFor="whitelisted_ips">
                Whitelist IP Addresses (Optional)
                <span style={{ fontSize: '12px', color: '#a0aec0', marginLeft: '10px' }}>
                  (Additional security layer for HMAC)
                </span>
              </label>
              <textarea
                id="whitelisted_ips"
                name="whitelisted_ips"
                value={formData.whitelisted_ips}
                onChange={handleInputChange}
                className="form-textarea"
                rows={3}
                placeholder="Enter IP addresses separated by commas (optional - adds extra security)"
              />
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#a0aec0' }}>
                Optional: Restrict HMAC-signed postbacks to specific IPs for additional security.
              </div>
            </div>
          )}

          {/* Affiliate Access Control Section */}
          <div className="form-section" style={{ marginTop: '30px', padding: '20px', backgroundColor: '#2d3748', borderRadius: '8px' }}>
            <h2 style={{ marginBottom: '10px', color: '#ecf0f1' }}>Affiliate Access Control</h2>
            <p style={{ color: '#a0aec0', fontSize: '14px', marginBottom: '20px' }}>
              Manage which affiliates are approved to work with this advertiser
            </p>

            <div style={{ marginBottom: '20px' }}>
              <input
                type="text"
                placeholder="Search affiliates..."
                value={affiliateSearch}
                onChange={(e) => setAffiliateSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #5a6c7d',
                  borderRadius: '4px',
                  backgroundColor: '#4a5568',
                  color: '#ecf0f1',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Approve All / Block All Buttons */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
              <button
                type="button"
                onClick={handleApproveAllAffiliates}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Approve All ({blockedAffiliates.length})
              </button>
              <button
                type="button"
                onClick={handleBlockAllAffiliates}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Block All ({approvedAffiliates.length})
              </button>
            </div>

            <div style={{ display: 'flex', gap: '20px' }}>
              {/* Approved Affiliates */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h3 style={{ color: '#27ae60', fontSize: '16px', margin: 0 }}>
                    Approved ({approvedAffiliates.length})
                  </h3>
                  {selectedApprovedAffiliates.size > 0 && (
                    <button
                      type="button"
                      onClick={handleBlockSelected}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      Block Selected ({selectedApprovedAffiliates.size})
                    </button>
                  )}
                </div>
                <div style={{
                  maxHeight: '250px',
                  overflowY: 'auto',
                  border: '1px solid #5a6c7d',
                  borderRadius: '4px',
                  padding: '10px',
                  backgroundColor: '#3c4855'
                }}>
                  {approvedAffiliates.length === 0 ? (
                    <div style={{ color: '#a0aec0', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
                      {affiliateSearch ? 'No matching approved affiliates' : 'No approved affiliates'}
                    </div>
                  ) : (
                    approvedAffiliates.map((affiliate) => (
                      <div key={affiliate.id} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px',
                        borderBottom: '1px solid #5a6c7d',
                        backgroundColor: '#4a5568',
                        marginBottom: '8px',
                        borderRadius: '4px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                          <input
                            type="checkbox"
                            checked={selectedApprovedAffiliates.has(affiliate.id)}
                            onChange={() => toggleApprovedSelection(affiliate.id)}
                            style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                          />
                          <div>
                            <div style={{ color: '#ecf0f1', fontSize: '14px', fontWeight: '500' }}>{affiliate.name}</div>
                            <div style={{ color: '#a0aec0', fontSize: '12px' }}>{affiliate.email}</div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleAffiliateStatus(affiliate.id)}
                          className="btn btn-danger btn-sm"
                          style={{ fontSize: '12px', padding: '6px 12px' }}
                        >
                          Block
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Blocked Affiliates */}
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h3 style={{ color: '#e74c3c', fontSize: '16px', margin: 0 }}>
                    Blocked ({blockedAffiliates.length})
                  </h3>
                  {selectedBlockedAffiliates.size > 0 && (
                    <button
                      type="button"
                      onClick={handleApproveSelected}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#27ae60',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      Approve Selected ({selectedBlockedAffiliates.size})
                    </button>
                  )}
                </div>
                <div style={{
                  maxHeight: '250px',
                  overflowY: 'auto',
                  border: '1px solid #5a6c7d',
                  borderRadius: '4px',
                  padding: '10px',
                  backgroundColor: '#3c4855'
                }}>
                  {blockedAffiliates.length === 0 ? (
                    <div style={{ color: '#a0aec0', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
                      {affiliateSearch ? 'No matching blocked affiliates' : 'No blocked affiliates'}
                    </div>
                  ) : (
                    blockedAffiliates.map((affiliate) => (
                      <div key={affiliate.id} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px',
                        borderBottom: '1px solid #5a6c7d',
                        backgroundColor: '#4a5568',
                        marginBottom: '8px',
                        borderRadius: '4px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                          <input
                            type="checkbox"
                            checked={selectedBlockedAffiliates.has(affiliate.id)}
                            onChange={() => toggleBlockedSelection(affiliate.id)}
                            style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                          />
                          <div>
                            <div style={{ color: '#ecf0f1', fontSize: '14px', fontWeight: '500' }}>{affiliate.name}</div>
                            <div style={{ color: '#a0aec0', fontSize: '12px' }}>{affiliate.email}</div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleAffiliateStatus(affiliate.id)}
                          className="btn btn-success btn-sm"
                          style={{ fontSize: '12px', padding: '6px 12px' }}
                        >
                          Approve
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Update Advertiser
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAdvertiser;